import React, { useCallback, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Pencil, Archive, Trash2, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import PullToRefresh from '../../components/PullToRefresh'
import { useExercise } from '../../hooks/useExercise'
import { useExercises } from '../../hooks/useExercises'
import { useExerciseProgression } from '../../hooks/useExerciseProgression'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { performRefresh } from '../../lib/refresh'
import WorkoutBackHeader from './components/WorkoutBackHeader'
import ExerciseForm from './components/ExerciseForm'
import ProgressionChart from './components/ProgressionChart'
import PRBadges from './components/PRBadges'
import ExercisePlaceholder from './components/ExercisePlaceholder'

function formatShortDate(iso, lang) {
  if (!iso) return ''
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(lang || 'en', {
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

export default function ExerciseDetail() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const { exercise, loading, refetch } = useExercise(id)
  const { updateExercise, archiveExercise, refetch: refetchAll } = useExercises()
  const handleRefresh = useCallback(() => performRefresh([refetch, refetchAll]), [refetch, refetchAll])
  const { points, allSets } = useExerciseProgression(id)
  const confirm = useConfirm()
  const [editing, setEditing] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // 5 most-recent sessions that touched this exercise, newest first.
  const recentSessions = [...points]
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .slice(0, 5)

  const handleUpdate = async (values) => {
    setSubmitting(true)
    const ok = await updateExercise(id, values)
    setSubmitting(false)
    if (ok) setEditing(false)
  }

  const handleArchive = async () => {
    const ok = await confirm({
      title: t('workout.exercises.confirmArchive'),
      description: t('workout.exercises.confirmArchiveDesc'),
      confirmLabel: t('workout.exercises.archive'),
      variant: 'destructive',
      icon: Archive,
    })
    if (ok) {
      await archiveExercise(id)
      navigate('/workout/exercises')
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 content-fade">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!exercise) {
    return (
      <div className="space-y-4 content-fade">
        <WorkoutBackHeader title={t('workout.exercises.notFound')} fallback="/workout/exercises" />
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade">
      <WorkoutBackHeader
        title={exercise.name}
        subtitle={`${t(`workout.muscleGroup.${exercise.muscle_group}`)} · ${t(`workout.equipment.${exercise.equipment_type}`)}`}
        fallback="/workout/exercises"
        action={
          !editing && (
            <Button
              onClick={() => setEditing(true)}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <Pencil className="w-3.5 h-3.5 me-1.5" />
              {t('workout.exercises.edit')}
            </Button>
          )
        }
      />

      {editing ? (
        <Card>
          <CardContent className="p-4 sm:p-6">
            <ExerciseForm
              initialValues={exercise}
              idForUpload={exercise.id}
              currentPhotoUrl={exercise.photoUrl}
              onSubmit={handleUpdate}
              onCancel={() => setEditing(false)}
              submitting={submitting}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Photo */}
          <Card>
            <CardContent className="p-0 overflow-hidden rounded-lg">
              {exercise.photoUrl ? (
                <img
                  src={exercise.photoUrl}
                  alt={exercise.name}
                  className="w-full max-h-72 object-contain bg-white"
                />
              ) : (
                <div className="h-48">
                  <ExercisePlaceholder
                    muscleGroup={exercise.muscle_group}
                    iconClassName="w-16 h-16"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Setup notes */}
          {exercise.setup_notes && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1.5">
                  {t('workout.exercises.detail.setupNotes')}
                </p>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {exercise.setup_notes}
                </p>
              </CardContent>
            </Card>
          )}

          {/* PR badges + chart */}
          <PRBadges sets={allSets} />
          <ProgressionChart points={points} />

          {/* Recent sessions */}
          <Card>
            <CardContent className="p-3 sm:p-4">
              <p className="text-xs text-muted-foreground mb-2">
                {t('workout.chart.recentSessions')}
              </p>
              {recentSessions.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2 text-center">
                  {t('workout.chart.noRecent')}
                </p>
              ) : (
                <ul className="space-y-1">
                  {recentSessions.map((p) => (
                    <li key={p.sessionId}>
                      <Link
                        to={`/workout/sessions/${p.sessionId}`}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors"
                      >
                        <span className="text-xs text-muted-foreground flex-shrink-0 w-16">
                          {formatShortDate(p.date, i18n.language)}
                        </span>
                        <span className="text-sm font-mono text-foreground" dir="ltr">
                          {Number(p.weight_kg).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg × {p.reps}
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 ms-auto" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Archive */}
          <Button
            onClick={handleArchive}
            variant="outline"
            className="w-full text-red-500 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4 me-2" />
            {t('workout.exercises.archive')}
          </Button>
        </>
      )}
    </div>
    </PullToRefresh>
  )
}
